import { FilterOperator, type RoleRequirementFilter } from './../types';
import { transformQuestionnaireIntoFilterQuestions } from './mappers';
import {
  type IQuestionnaireAnswer,
  type IQuestionnaireQuestion,
  QuestionnaireAnswer,
  QuestionnaireQuestion,
  type IQuestionnaireStore,
} from './../../JobStore/QuestionnaireStore';

import { type Instance, types, getParentOfType } from 'mobx-state-tree';
import { set } from 'mobx';
import { ANSWER_TYPE } from '../../../constants/roleRequirementsTypes';
import type { RoleRequirementFilterCount } from '../../../graphql/fetchProspectsWithPaginationByJobId';

const StandardFilterAnswer = types
  .model({
    answerInfo: QuestionnaireAnswer,
    isSelected: types.optional(types.boolean, false),
    totalCount: types.optional(types.number, 0),
  })
  .views((self) => ({
    get count(): number {
      return self.totalCount;
    },
    get disabled(): boolean {
      return this.count === 0 && !self.isSelected;
    },
  }))
  .actions((self) => ({
    onToggleAnswerSelected: () => {
      self.isSelected = !self.isSelected;
      const question = getParentOfType(self, FilterQuestion);
      question.onToggleAnswers();
    },
  }));

export interface IStandardFilterAnswer
  extends Instance<typeof StandardFilterAnswer> {}

const SimplifiedFilterAnswer = types
  .model({
    answerInfo: QuestionnaireAnswer,
    isSelected: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get count(): number {
      const question = getParentOfType(self, FilterQuestion);
      const answerIds = self.answerInfo.answerGlobalIdsOrIds;

      if (answerIds) {
        return answerIds
          .map((hardcodedAnswerId) => {
            const answer = question.answers.find(
              (item) =>
                item.answerInfo.id === hardcodedAnswerId ||
                item.answerInfo.globalId === hardcodedAnswerId,
            );
            return answer?.count ?? 0;
          })
          .reduce((a, b) => a + b, 0);
      }

      return 0;
    },
    get disabled(): boolean {
      return this.count === 0 && !self.isSelected;
    },
  }))
  .actions((self) => ({
    onToggleAnswerSelected: () => {
      self.isSelected = !self.isSelected;
      const question = getParentOfType(self, FilterQuestion);
      self.answerInfo.answerGlobalIdsOrIds?.forEach(
        (hardcodedAnswerId: string) => {
          const answer = question.answers.find(
            (item) =>
              item.answerInfo.globalId === hardcodedAnswerId ||
              item.answerInfo.id === hardcodedAnswerId,
          );
          answer?.onToggleAnswerSelected();
        },
      );
      question.onToggleAnswers();
    },
  }));

export const FilterQuestion = types
  .model({
    questionInfo: QuestionnaireQuestion,
    answers: types.array(StandardFilterAnswer),
    isSelected: types.optional(types.boolean, false),
    isExpanded: types.optional(types.boolean, true),
    showPreferredAnswersMessage: types.optional(types.boolean, false),
    totalCount: types.optional(types.number, 0),
    simplifiedAnswers: types.array(SimplifiedFilterAnswer),
  })
  .views((self) => ({
    get selectedAnswers(): IQuestionnaireAnswer[] {
      return self.answers
        .filter((a) => a.isSelected)
        .map((answer) => answer.answerInfo);
    },
    get answersToDisplay() {
      return self.simplifiedAnswers.length > 0
        ? self.simplifiedAnswers
        : self.answers;
    },
    get hasSimplifiedAnswers() {
      return self.simplifiedAnswers.length > 0;
    },
    get hasPreferredAnswers() {
      return (
        this.answersToDisplay.filter((answer) => answer.answerInfo.isPreferred)
          .length > 0
      );
    },
    get hasSelectablePreferredAnswers() {
      return (
        this.answersToDisplay.filter(
          (answer) => answer.answerInfo.isPreferred && answer.count > 0,
        ).length > 0
      );
    },
    get hasAnyNonPreferredAnswersSelected() {
      return (
        this.answersToDisplay.filter(
          (answer) => !answer.answerInfo.isPreferred && answer.isSelected,
        ).length > 0
      );
    },
    get hasAllPreferredAnswersSelected() {
      if (this.hasAnyNonPreferredAnswersSelected) {
        return false;
      }
      return this.answersToDisplay
        .filter((answer) => answer.answerInfo.isPreferred)
        .every((answer) => answer.isSelected);
    },
    get disabled(): boolean {
      return this.answersToDisplay.every(
        (answer) => answer.count === 0 && !answer.isSelected,
      );
    },
    get minOrderSelected() {
      if (self.answers.length === 0) {
        return -1;
      }
      return (
        self.answers.find((answer) => answer.isSelected)?.answerInfo.order ?? 1
      );
    },
    get maxOrderSelected() {
      if (self.answers.length === 0) {
        return -1;
      }
      const reversedAnswers = self.answers.slice().reverse();
      const lastAnswer = self.answers[self.answers.length - 1];
      return (
        reversedAnswers.find((answer) => answer.isSelected)?.answerInfo.order ??
        lastAnswer.answerInfo.order
      );
    },
    get selectedAnswersCount() {
      const selectedAnswers = self.answers.filter((a) => a.isSelected);

      const hasSelectedAnswers = selectedAnswers.length > 0;

      const answersToCount = hasSelectedAnswers
        ? selectedAnswers
        : self.answers;

      return answersToCount.reduce((total, answer) => total + answer.count, 0);
    },
  }))
  .actions((self) => ({
    onToggleQuestionSelected: () => {
      if (self.isSelected) {
        self.isSelected = !self.isSelected;
        self.showPreferredAnswersMessage = false;
        self.answersToDisplay
          .filter((a) => a.isSelected)
          .forEach((answer) => {
            answer.onToggleAnswerSelected();
          });
      } else {
        self.isSelected = !self.isSelected;
        self.isExpanded = true;
        if (self.hasSelectablePreferredAnswers) {
          self.answersToDisplay.forEach((answer) => {
            if (answer.answerInfo.isPreferred && answer.count > 0) {
              answer.onToggleAnswerSelected();
            }
          });
          self.showPreferredAnswersMessage = true;
        } else {
          self.answersToDisplay.forEach((answer) => {
            if (answer.count > 0) {
              answer.onToggleAnswerSelected();
            }
          });
        }
      }
    },
    onToggleAnswers: () => {
      self.isSelected = self.answersToDisplay.some(
        (answer) => answer.isSelected,
      );
      self.showPreferredAnswersMessage = false;
    },
    onToggleQuestionExpanded: () => {
      self.isExpanded = !self.isExpanded;
    },
    onToggleSimplifiedAnswersSelected: (answerIds: string[]) => {
      answerIds.forEach((answerId: string) =>
        self.answers.forEach((answer) => {
          if (answer.answerInfo.id === answerId) {
            answer.onToggleAnswerSelected();
          }
        }),
      );
    },
    selectOnlyAnswersInRange: (minOrder: number, maxOrder: number) => {
      self.isSelected = true;
      self.showPreferredAnswersMessage = false;

      self.answers.forEach((a) => {
        a.isSelected =
          a.answerInfo.order >= minOrder &&
          a.answerInfo.order <= maxOrder &&
          !a.disabled;
      });
    },
    clearSelectedAnswers: () => {
      self.answers.forEach((answer) => {
        answer.isSelected = false;
      });
      self.simplifiedAnswers.forEach((answer) => {
        answer.isSelected = false;
      });
    },
  }));

export interface IFilterQuestion extends Instance<typeof FilterQuestion> {}

const FiltersStore = types
  .model({
    questions: types.optional(types.array(FilterQuestion), []),
  })
  .views((self) => ({
    get selectedQuestionAnswers() {
      const rrFilters: Array<{
        question: IQuestionnaireQuestion;
        answers: IQuestionnaireAnswer[];
      }> = [];

      self.questions.forEach((question) => {
        const selectedAnswers = question.selectedAnswers;
        if (selectedAnswers.length > 0) {
          rrFilters.push({
            question: question.questionInfo,
            answers: selectedAnswers,
          });
        }
      });
      return rrFilters;
    },
    get selectedRrFilters(): RoleRequirementFilter[] {
      const rrGlobalFilters: RoleRequirementFilter[] = [];

      this.selectedQuestionAnswers.forEach(({ question, answers }) => {
        if (answers.length > 0) {
          rrGlobalFilters.push({
            questionId: question.globalIdOrId,
            operator:
              question.answerType === ANSWER_TYPE.MULTISELECT
                ? FilterOperator.AND
                : FilterOperator.OR,
            answerIds: answers.map((answer) => answer.globalIdOrId),
          });
        }
      });
      return rrGlobalFilters;
    },
    get hasSelectedFilter() {
      return this.selectedRrFilters.length > 0;
    },
    get hasFilterQuestions() {
      return self.questions.length > 0;
    },
    get isMustHaveToggleSelected() {
      return self.questions.every((question) => {
        if (question.questionInfo.essential) {
          return question.hasAllPreferredAnswersSelected;
        }
        return !question.isSelected;
      });
    },
    get shouldShowMustHaveToggle() {
      return (
        self.questions.filter((question) => question.questionInfo.essential)
          .length > 0
      );
    },
  }))
  .actions((self) => ({
    clearFilters: () => {
      self.questions.forEach((question) => {
        question.isSelected = false;
        question.showPreferredAnswersMessage = false;
        question.clearSelectedAnswers();
      });
    },
    updateRRAnswerCounts: (counts: RoleRequirementFilterCount[] = []) => {
      self.questions.forEach((question) => {
        question.answers.forEach((answer) => {
          const totalCount = counts.find(
            (count) =>
              count.id === answer.answerInfo.id ||
              count.id === answer.answerInfo.globalId,
          );
          answer.totalCount = totalCount ? totalCount.count : 0;
        });
      });
    },
    updateRRAnswerCount: (id: string, changeAmount: number) => {
      for (const question of self.questions) {
        const matchingAnswer = question.answers.find(
          (answer) =>
            answer.answerInfo.id === id || answer.answerInfo.globalId === id,
        );

        if (matchingAnswer) {
          matchingAnswer.totalCount = Math.max(
            0,
            matchingAnswer.totalCount + changeAmount,
          );
          return;
        }
      }
    },
    load: (questionnaire: IQuestionnaireStore) => {
      const questions =
        transformQuestionnaireIntoFilterQuestions(questionnaire);
      set(self, { questions });
    },
  }))
  .actions((self) => ({
    enableMustHaveFilter: () => {
      self.clearFilters();
      self.questions
        .filter((question) => question.questionInfo.essential)
        .forEach((question) => {
          question.isSelected = true;
          question.isExpanded = true;
          question.showPreferredAnswersMessage = true;
          question.answersToDisplay.forEach((answer) => {
            if (answer.answerInfo.isPreferred) {
              answer.onToggleAnswerSelected();
            }
          });
        });
    },
  }))
  .actions((self) => ({
    toggleMustHaveFilter: () => {
      if (self.isMustHaveToggleSelected) {
        self.clearFilters();
        return;
      }
      self.enableMustHaveFilter();
    },
  }));

export interface IFiltersStore extends Instance<typeof FiltersStore> {}
export default FiltersStore;
